import { useState } from 'react';
import {
  makeStyles,
  Box,
  Typography,
  Link,
  Button,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Edit as DraftIcon,
  ThumbUp as ApprovedIcon,
  MergeType as MergeIcon,
  Warning as ConflictIcon,
  OpenInNew as OpenInNewIcon,
  Check as ReadyIcon,
  PersonAdd as RequestReviewIcon,
} from '@material-ui/icons';
import { PullRequestReview, PullRequestComment, PullRequestReviewItem } from '../../types';
import { DiffViewer } from './DiffViewer';
import { CommentsViewer } from './CommentsViewer';
import { ReviewsViewer } from './ReviewsViewer';
import { ReviewerSelector } from './ReviewerSelector';
import { AiPromptMenu } from '../AiPromptMenu/AiPromptMenu';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  root: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  draftRoot: {
    opacity: 0.7,
    borderStyle: 'dashed',
  },
  mainInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
    flexWrap: 'wrap',
  },
  prNumber: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    flex: 1,
  },
  title: {
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 300,
    fontSize: '0.875rem',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  externalLink: {
    color: theme.palette.text.secondary,
    padding: 2,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  author: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  },
  repo: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  },
  linesChanged: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
  },
  additions: {
    color: theme.palette.success.main,
  },
  deletions: {
    color: theme.palette.error.main,
  },
  statusIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  chip: {
    height: 20,
    fontSize: '0.7rem',
  },
  draftChip: {
    height: 20,
    fontSize: '0.7rem',
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.getContrastText(theme.palette.grey[400]),
  },
  approvedChip: {
    height: 20,
    fontSize: '0.7rem',
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  jiraChip: {
    height: 20,
    fontSize: '0.7rem',
    backgroundColor: '#0052CC',
    color: 'white',
    '&:hover': {
      backgroundColor: '#0065FF',
    },
  },
  terraformChip: {
    height: 20,
    fontSize: '0.7rem',
    backgroundColor: '#5C4EE5',
    color: 'white',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  actionButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.25, 1),
    fontSize: '0.75rem',
    textTransform: 'none',
  },
  mergeButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.25, 1),
    fontSize: '0.75rem',
    textTransform: 'none',
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  conflictsButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.25, 1),
    fontSize: '0.75rem',
    textTransform: 'none',
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.warning.dark,
    },
  },
  draftButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.25, 1),
    fontSize: '0.75rem',
    textTransform: 'none',
    backgroundColor: theme.palette.grey[400],
    color: theme.palette.getContrastText(theme.palette.grey[400]),
    '&:hover': {
      backgroundColor: theme.palette.grey[500],
    },
  },
  readyButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.25, 1),
    fontSize: '0.75rem',
    textTransform: 'none',
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.success.main,
    },
  },
  metaInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  draftIcon: {
    color: theme.palette.grey[500],
  },
  approvedIcon: {
    color: theme.palette.success.main,
  },
}));

interface PullRequestListItemProps {
  pr: PullRequestReview;
  currentUser?: string;
  onMerge?: (pr: PullRequestReview, mergeMethod: 'merge' | 'squash' | 'rebase') => Promise<void>;
  onMarkReady?: (pr: PullRequestReview) => Promise<void>;
  onConvertToDraft?: (pr: PullRequestReview) => Promise<void>;
  onFetchDiff?: (pr: PullRequestReview) => Promise<string>;
  onFetchComments?: (pr: PullRequestReview) => Promise<PullRequestComment[]>;
  onAddComment?: (pr: PullRequestReview, body: string) => Promise<void>;
  onFetchReviews?: (pr: PullRequestReview) => Promise<PullRequestReviewItem[]>;
  onAddReview?: (pr: PullRequestReview, body: string, event: 'COMMENT' | 'APPROVE' | 'REQUEST_CHANGES') => Promise<void>;
  onRefreshPR?: (pr: PullRequestReview) => Promise<void>;
  onRequestReviewers?: (
    pr: PullRequestReview,
    reviewers: { users: string[]; teams: string[] },
  ) => Promise<void>;
}

export const PullRequestListItem = ({
  pr,
  currentUser,
  onMerge,
  onMarkReady,
  onConvertToDraft,
  onFetchDiff,
  onFetchComments,
  onAddComment,
  onFetchReviews,
  onAddReview,
  onRefreshPR,
  onRequestReviewers,
}: PullRequestListItemProps) => {
  const classes = useStyles();
  const [mergeMenuAnchor, setMergeMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [merging, setMerging] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  // Diff state
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffText, setDiffText] = useState<string | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);

  // Comments state
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState<PullRequestComment[] | null>(null);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  // Reviews state
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [reviews, setReviews] = useState<PullRequestReviewItem[] | null>(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState<string | null>(null);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // Reviewer selector state
  const [reviewerSelectorOpen, setReviewerSelectorOpen] = useState(false);
  const [reviewerSubmitting, setReviewerSubmitting] = useState(false);

  const renderStatusIcon = () => {
    if (!pr.statusChecks) {
      return (
        <Tooltip title="No status checks">
          <PendingIcon fontSize="small" style={{ color: '#9e9e9e' }} />
        </Tooltip>
      );
    }

    const { state, firstFailedCheckUrl, checksUrl } = pr.statusChecks;

    if (state === 'success') {
      return (
        <Tooltip title="All checks passed">
          <Link href={checksUrl} target="_blank" rel="noopener" onClick={e => e.stopPropagation()}>
            <CheckCircleIcon fontSize="small" style={{ color: '#4caf50' }} />
          </Link>
        </Tooltip>
      );
    }

    if (state === 'failure' || state === 'error') {
      return (
        <Tooltip title="Checks failed - click to view">
          <Link href={firstFailedCheckUrl || checksUrl} target="_blank" rel="noopener" onClick={e => e.stopPropagation()}>
            <ErrorIcon fontSize="small" style={{ color: '#f44336' }} />
          </Link>
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Checks in progress">
        <Link href={checksUrl} target="_blank" rel="noopener" onClick={e => e.stopPropagation()}>
          <PendingIcon fontSize="small" style={{ color: '#ff9800' }} />
        </Link>
      </Tooltip>
    );
  };

  const handleMergeClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMergeMenuAnchor(event.currentTarget);
  };

  const handleMergeClose = () => {
    setMergeMenuAnchor(null);
  };

  const handleMergeSelect = async (method: 'merge' | 'squash' | 'rebase') => {
    handleMergeClose();
    if (onMerge) {
      setMerging(true);
      try {
        await onMerge(pr, method);
      } finally {
        setMerging(false);
      }
    }
  };

  const handleStatusClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setStatusMenuAnchor(event.currentTarget);
  };

  const handleStatusClose = () => {
    setStatusMenuAnchor(null);
  };

  const handleMarkReady = async () => {
    handleStatusClose();
    if (onMarkReady) {
      setChangingStatus(true);
      try {
        await onMarkReady(pr);
        // Refresh the PR listing to update draft status
        if (onRefreshPR) {
          await onRefreshPR(pr);
        }
      } finally {
        setChangingStatus(false);
      }
    }
  };

  const handleConvertToDraft = async () => {
    handleStatusClose();
    if (onConvertToDraft) {
      setChangingStatus(true);
      try {
        await onConvertToDraft(pr);
        // Refresh the PR listing to update draft status
        if (onRefreshPR) {
          await onRefreshPR(pr);
        }
      } finally {
        setChangingStatus(false);
      }
    }
  };

  const handleTitleClick = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (diffOpen) {
      setDiffOpen(false);
      return;
    }

    setDiffOpen(true);

    if (!diffText && onFetchDiff) {
      setDiffLoading(true);
      setDiffError(null);
      try {
        const diff = await onFetchDiff(pr);
        setDiffText(diff);
      } catch (error) {
        setDiffError(error instanceof Error ? error.message : 'Failed to fetch diff');
      } finally {
        setDiffLoading(false);
      }
    }
  };

  const handleCloseDiff = () => {
    setDiffOpen(false);
  };

  const handleCommentClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (commentsOpen) {
      setCommentsOpen(false);
      return;
    }

    setCommentsOpen(true);
    // Close other accordions
    setDiffOpen(false);
    setReviewsOpen(false);

    if (!comments && onFetchComments) {
      setCommentsLoading(true);
      setCommentsError(null);
      try {
        const fetchedComments = await onFetchComments(pr);
        setComments(fetchedComments);
      } catch (error) {
        setCommentsError(error instanceof Error ? error.message : 'Failed to fetch comments');
      } finally {
        setCommentsLoading(false);
      }
    }
  };

  const handleCloseComments = () => {
    setCommentsOpen(false);
  };

  const handleAddComment = async (body: string) => {
    if (onAddComment) {
      setCommentSubmitting(true);
      try {
        await onAddComment(pr, body);
        // Refresh comments
        if (onFetchComments) {
          const fetchedComments = await onFetchComments(pr);
          setComments(fetchedComments);
        }
        // Refresh the PR listing to update comment count
        if (onRefreshPR) {
          await onRefreshPR(pr);
        }
      } finally {
        setCommentSubmitting(false);
      }
    }
  };

  const handleReviewClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (reviewsOpen) {
      setReviewsOpen(false);
      return;
    }

    setReviewsOpen(true);
    // Close other accordions
    setDiffOpen(false);
    setCommentsOpen(false);

    if (!reviews && onFetchReviews) {
      setReviewsLoading(true);
      setReviewsError(null);
      try {
        const fetchedReviews = await onFetchReviews(pr);
        setReviews(fetchedReviews);
      } catch (error) {
        setReviewsError(error instanceof Error ? error.message : 'Failed to fetch reviews');
      } finally {
        setReviewsLoading(false);
      }
    }
  };

  const handleCloseReviews = () => {
    setReviewsOpen(false);
  };

  const handleAddReview = async (body: string, event: 'COMMENT' | 'APPROVE' | 'REQUEST_CHANGES') => {
    if (onAddReview) {
      setReviewSubmitting(true);
      try {
        await onAddReview(pr, body, event);
        // Refresh reviews
        if (onFetchReviews) {
          const fetchedReviews = await onFetchReviews(pr);
          setReviews(fetchedReviews);
        }
        // Refresh the PR listing to update review count and approval status
        if (onRefreshPR) {
          await onRefreshPR(pr);
        }
      } finally {
        setReviewSubmitting(false);
      }
    }
  };

  const handleReviewerSelectorClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (reviewerSelectorOpen) {
      setReviewerSelectorOpen(false);
      return;
    }

    setReviewerSelectorOpen(true);
    // Close other accordions
    setDiffOpen(false);
    setCommentsOpen(false);
    setReviewsOpen(false);
  };

  const handleCloseReviewerSelector = () => {
    setReviewerSelectorOpen(false);
  };

  const handleRequestReviewers = async (
    reviewers: { users: string[]; teams: string[] },
  ) => {
    if (onRequestReviewers) {
      setReviewerSubmitting(true);
      try {
        await onRequestReviewers(pr, reviewers);
        setReviewerSelectorOpen(false);
        // Refresh the PR listing
        if (onRefreshPR) {
          await onRefreshPR(pr);
        }
      } finally {
        setReviewerSubmitting(false);
      }
    }
  };

  const totalLines = pr.additions + pr.deletions;
  const branchUrl = `https://github.com/${pr.repository.fullName}/tree/${pr.headRef}`;
  const commitsUrl = `https://github.com/${pr.repository.fullName}/commits/${pr.headRef}`;
  const filesChangedUrl = `${pr.htmlUrl}/files`;
  const conflictsUrl = `${pr.htmlUrl}/conflicts`;

  // Check if PR has conflicts
  const hasConflicts = !pr.mergeable && pr.mergeableState === 'dirty';

  // Determine which merge methods are available
  const mergeMethodsAvailable = pr.allowedMergeMethods || { merge: true, squash: true, rebase: true };

  // Check if user can approve (can't self-approve)
  const isAuthor = currentUser && pr.author.login === currentUser;
  const canApprove = !isAuthor;
  const canRequestChanges = !isAuthor;

  return (
    <Box className={classes.container}>
      <Box className={`${classes.root} ${pr.draft ? classes.draftRoot : ''}`}>
        <Box className={classes.mainInfo}>
          {/* Status Icon */}
          <Box className={classes.statusIcon}>
            {renderStatusIcon()}
          </Box>

          {/* Draft Icon */}
          {pr.draft && (
            <Tooltip title="Draft PR">
              <DraftIcon fontSize="small" className={classes.draftIcon} />
            </Tooltip>
          )}

          {/* Approved Icon */}
          {pr.isApproved && !pr.draft && (
            <Tooltip title={`Approved (${pr.approvalCount} approval${pr.approvalCount !== 1 ? 's' : ''})`}>
              <ApprovedIcon fontSize="small" className={classes.approvedIcon} />
            </Tooltip>
          )}

          {/* PR Number */}
          <Typography className={classes.prNumber}>
            #{pr.number}
          </Typography>

          {/* PR Title - Click to show diff accordion */}
          <Box className={classes.titleContainer}>
            <Tooltip title={`${pr.title} - Click to ${diffOpen ? 'hide' : 'view'} diff`}>
              <Typography
                className={classes.title}
                onClick={handleTitleClick}
              >
                {pr.title}
              </Typography>
            </Tooltip>
            {/* External link to GitHub Files Changed */}
            <Tooltip title="Open in GitHub">
              <IconButton
                size="small"
                className={classes.externalLink}
                href={filesChangedUrl}
                target="_blank"
                rel="noopener"
                onClick={e => e.stopPropagation()}
              >
                <OpenInNewIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box className={classes.metaInfo}>
          {/* Author */}
          <Tooltip title={`View commits by ${pr.author.login}`}>
            <Link
              href={commitsUrl}
              target="_blank"
              rel="noopener"
              className={classes.author}
              onClick={e => e.stopPropagation()}
            >
              {pr.author.login}
            </Link>
          </Tooltip>

          {/* Repository */}
          <Tooltip title="View branch">
            <Link
              href={branchUrl}
              target="_blank"
              rel="noopener"
              className={classes.repo}
              onClick={e => e.stopPropagation()}
            >
              {pr.repository.fullName}
            </Link>
          </Tooltip>

          {/* Lines Changed */}
          <Tooltip title={`${pr.changedFiles} files changed`}>
            <Link
              href={filesChangedUrl}
              target="_blank"
              rel="noopener"
              className={classes.linesChanged}
              onClick={e => e.stopPropagation()}
            >
              <span className={classes.additions}>+{pr.additions}</span>
              <span className={classes.deletions}>-{pr.deletions}</span>
              <span>({totalLines})</span>
            </Link>
          </Tooltip>

          {/* JIRA Tickets */}
          {pr.jiraTickets && pr.jiraTickets.length > 0 && pr.jiraTickets.map(ticket => (
            <Chip
              key={ticket}
              label={ticket}
              size="small"
              className={classes.jiraChip}
              component="a"
              href={`https://jira.atlassian.com/browse/${ticket}`}
              target="_blank"
              rel="noopener"
              clickable
              onClick={e => e.stopPropagation()}
            />
          ))}

          {/* Terraform Workspace */}
          {pr.terraformWorkspace && (
            <Chip
              label={`TF: ${pr.terraformWorkspace}`}
              size="small"
              className={classes.terraformChip}
            />
          )}

          {/* Approved chip */}
          {pr.isApproved && !pr.draft && (
            <Chip
              icon={<ApprovedIcon style={{ fontSize: 14 }} />}
              label={`Approved${pr.approvalCount > 1 ? ` (${pr.approvalCount})` : ''}`}
              size="small"
              className={classes.approvedChip}
            />
          )}
        </Box>

        {/* Action Buttons */}
        <Box className={classes.actions}>
          {/* Draft/Ready button - anyone can change status (useful for bot-created PRs) */}
          {(onMarkReady || onConvertToDraft) && (
            <>
              <Button
                variant="contained"
                size="small"
                className={pr.draft ? classes.draftButton : classes.readyButton}
                onClick={handleStatusClick}
                disabled={changingStatus}
                startIcon={changingStatus ? <CircularProgress size={14} color="inherit" /> : (pr.draft ? <DraftIcon /> : <ReadyIcon />)}
              >
                {pr.draft ? 'Draft' : 'Ready'}
              </Button>
              <Menu
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={handleStatusClose}
                onClick={e => e.stopPropagation()}
              >
                {pr.draft ? (
                  <MenuItem onClick={handleMarkReady} disabled={!onMarkReady}>
                    <ReadyIcon fontSize="small" style={{ marginRight: 8 }} />
                    Ready for review
                  </MenuItem>
                ) : (
                  <MenuItem onClick={handleConvertToDraft} disabled={!onConvertToDraft}>
                    <DraftIcon fontSize="small" style={{ marginRight: 8 }} />
                    Convert to draft
                  </MenuItem>
                )}
              </Menu>
            </>
          )}

          {/* Conflicts button - show when PR has merge conflicts */}
          {hasConflicts && (
            <Button
              variant="contained"
              size="small"
              className={classes.conflictsButton}
              href={conflictsUrl}
              target="_blank"
              rel="noopener"
              onClick={e => e.stopPropagation()}
              startIcon={<ConflictIcon />}
            >
              Conflicts
            </Button>
          )}

          {/* Merge button - only for user's PRs that are ready to merge */}
          {pr.isReadyToMerge && pr.relationship !== 'review_requested' && onMerge && (
            <>
              <Button
                variant="contained"
                size="small"
                className={classes.mergeButton}
                onClick={handleMergeClick}
                disabled={merging}
                startIcon={merging ? <CircularProgress size={14} color="inherit" /> : <MergeIcon />}
              >
                Merge
              </Button>
              <Menu
                anchorEl={mergeMenuAnchor}
                open={Boolean(mergeMenuAnchor)}
                onClose={handleMergeClose}
                onClick={e => e.stopPropagation()}
              >
                {mergeMethodsAvailable.squash && (
                  <MenuItem onClick={() => handleMergeSelect('squash')}>
                    Squash and merge
                  </MenuItem>
                )}
                {mergeMethodsAvailable.rebase && (
                  <MenuItem onClick={() => handleMergeSelect('rebase')}>
                    Rebase and merge
                  </MenuItem>
                )}
                {mergeMethodsAvailable.merge && (
                  <MenuItem onClick={() => handleMergeSelect('merge')}>
                    Create a merge commit
                  </MenuItem>
                )}
              </Menu>
            </>
          )}

          <Button
            variant="contained"
            color="primary"
            size="small"
            className={classes.actionButton}
            onClick={handleReviewClick}
          >
            Review{pr.reviewCount > 0 ? ` (${pr.reviewCount})` : ''}
          </Button>
          <Button
            variant="outlined"
            size="small"
            className={classes.actionButton}
            onClick={handleCommentClick}
          >
            Comment{pr.commentCount > 0 ? ` (${pr.commentCount})` : ''}
          </Button>
          {onRequestReviewers && (
            <Tooltip title="Request review from users or teams">
              <Button
                variant="outlined"
                size="small"
                className={classes.actionButton}
                onClick={handleReviewerSelectorClick}
                startIcon={<RequestReviewIcon />}
              >
                Request
              </Button>
            </Tooltip>
          )}
          <AiPromptMenu
            repository={pr.repository.fullName}
            branch={pr.headRef}
            prNumber={pr.number}
            label="pr"
          />
        </Box>
      </Box>

      {/* Diff Viewer Accordion */}
      <DiffViewer
        diffText={diffText}
        loading={diffLoading}
        error={diffError}
        open={diffOpen}
        onClose={handleCloseDiff}
        filesChanged={pr.changedFiles}
        additions={pr.additions}
        deletions={pr.deletions}
      />

      {/* Comments Viewer Accordion */}
      <CommentsViewer
        comments={comments}
        loading={commentsLoading}
        error={commentsError}
        open={commentsOpen}
        onClose={handleCloseComments}
        onAddComment={handleAddComment}
        submitting={commentSubmitting}
      />

      {/* Reviews Viewer Accordion */}
      <ReviewsViewer
        reviews={reviews}
        loading={reviewsLoading}
        error={reviewsError}
        open={reviewsOpen}
        onClose={handleCloseReviews}
        onAddReview={handleAddReview}
        submitting={reviewSubmitting}
        canApprove={canApprove}
        canRequestChanges={canRequestChanges}
      />

      {/* Reviewer Selector Accordion */}
      {onRequestReviewers && (
        <ReviewerSelector
          open={reviewerSelectorOpen}
          onClose={handleCloseReviewerSelector}
          onRequestReviewers={handleRequestReviewers}
          submitting={reviewerSubmitting}
          repoOwner={pr.repository.owner}
          currentReviewers={pr.requestedReviewers}
        />
      )}
    </Box>
  );
};
